import { logger } from '@vagabondinternet/logging';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authErrorCode, authFailure, sharedAuthMessage } from '../api';

export interface SendMessages {
  /** Logged with the error code when sending fails. */
  failedLog: string;
  sent: string;
  /** Shown when sending fails for a reason without a message of its own. */
  failed: string;
}

/** How long a resend waits after a send, or after being rate limited. */
export const RESEND_COOLDOWN_SECONDS = 60;

export interface Resend {
  sending: boolean;
  /** Whole seconds until another send is allowed; 0 when it is. */
  cooldown: number;
  /** Announced in a role="status" line once an email is sent. */
  status?: string;
  /** Shown in a WordsHappenFormAlert when sending failed. */
  error?: string;
  /** Sends unless one is in flight or cooling down; resolves whether it sent. */
  resend: () => Promise<boolean>;
}

export interface ResendOptions {
  /** What a send logs and announces; rate-limit and network failures have their own message. */
  messages: SendMessages;
  /** Start cooling down at once, e.g. just after registration sent the first email. */
  coolingDown?: boolean;
}

const secondsUntil = (time: number) => Math.max(0, Math.ceil((time - Date.now()) / 1000));

/** When a cooldown starting now ends. */
const cooldownEnd = () => Date.now() + RESEND_COOLDOWN_SECONDS * 1000;

/**
 * Sends an email (verification, password reset, sign-in link) at most once per RESEND_COOLDOWN_SECONDS. A rate-limited send
 * (auth/too-many-requests) cools down too. Logs only the error code.
 */
export const useResend = (send: () => Promise<void>, { messages, coolingDown = false }: ResendOptions): Resend => {
  const [availableAt, setAvailableAt] = useState(() => (coolingDown ? Date.now() + RESEND_COOLDOWN_SECONDS * 1000 : 0));
  const [cooldown, setCooldown] = useState(() => secondsUntil(availableAt));
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<string>();
  const [error, setError] = useState<string>();
  const inFlight = useRef(false);

  useEffect(() => {
    setCooldown(secondsUntil(availableAt));
    if (availableAt <= Date.now()) {
      return;
    }
    const timer = setInterval(() => {
      const remaining = secondsUntil(availableAt);
      setCooldown(remaining);
      if (remaining === 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [availableAt]);

  const resend = useCallback(async () => {
    if (inFlight.current || secondsUntil(availableAt) > 0) {
      return false;
    }
    inFlight.current = true;
    setSending(true);
    setStatus(undefined);
    setError(undefined);
    try {
      await send();
      setStatus(messages.sent);
      setAvailableAt(cooldownEnd());
      return true;
    } catch (cause) {
      logger.error({ message: messages.failedLog, code: authErrorCode(cause) });
      const failure = authFailure(cause);
      setError(sharedAuthMessage(failure) ?? messages.failed);
      if (failure === 'too-many-requests') {
        setAvailableAt(cooldownEnd());
      }
      return false;
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }, [send, messages, availableAt]);

  return { sending, cooldown, status, error, resend };
};
